// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.
// The AUTHOR's card for their own note (status, edit, claim, views). Not to be confused with
// `OwnerNoteCard`, the public card every reader sees on the wall.
import { useEffect, useState } from 'react'
import clsx from 'clsx'
import styles from './note-card.module.css'
import ClaimCta from './ClaimCta'
import CoachingLines from './CoachingLines'
import OwnerHint from './OwnerHint'
import ReviewPhotos from './ReviewPhotos'
import NoteAnswers, { ownershipLine } from './NoteAnswers'
import ViewsRow from './ViewsRow'
import {
  OWN_CARD_HINT,
  OWN_PHOTO_HINT,
  OWN_PRIVATE_HINT,
  OWN_PRIVATE_LABEL,
  OWN_PSEUDONYM_HINT,
  statusHint,
} from 'lib/owner-notes/hints'
import {
  OWNER_DEFAULT_QUESTIONS,
  OWNER_HELD_REVIEW_MESSAGE,
  type OwnerClaimResponse,
  type OwnerNoteOwn,
  type OwnerNoteViews,
  type OwnerPhotoStatus,
  type OwnerQuestionSetDef,
} from 'lib/dealhunt/types'
import { useOwnNoteActions } from './useOwnNoteActions'
import { useKeepSurface } from 'lib/owner-notes/session'
import { viewsSummary } from 'lib/owner-notes/views'
import { useOwnerSurfaceTrack, type OwnerTrack } from 'lib/owner-notes/analytics'
import {
  heldClaimOffered,
  KEEP_CLAIM_FINE,
  keepClaimLead,
  keepClaimOffered,
  liveNoteNotice,
  OWN_EDIT_CHECKING_LINE,
  OWN_HELD_RETRY_LINE,
  OWN_DELETE_COPY,
  OWN_HIDDEN_LINE,
  OWN_PROOFREAD_LINE,
  OWN_PROOFREAD_UNDO,
  ownersDeepLink,
  ownNoteControls,
  ownStatusLabel,
  proofreadUndoOffered,
  SEE_ON_PAGE,
  shownOwnNote,
} from 'lib/owner-notes/band-copy'

interface OwnNoteCardProps {
  note: OwnerNoteOwn
  /** Claimed state of the device's profile (drives the held_review copy — #64). */
  claimed: boolean
  trackOwner: OwnerTrack
  /** Where the card sits (analytics `location` on claim_started). */
  location: 'own_note' | 'profile'
  /** Labels for the answers; the default set until the category's live set is known. */
  questions?: OwnerQuestionSetDef | null
  /** The product page, when the card sits elsewhere (category band): links to the Owners section. */
  productHref?: string | null
  /** The author's private count (#138/#140) — the shelf row's `views`; absent = no row. */
  views?: OwnerNoteViews | null
  /**
   * #151 — render `note.coaching.clue`. Every reader-facing surface wants it (My gear, the PDP
   * card, the category tile, the guide's seated card), so it defaults ON. The ONE caller that
   * passes false is `OwnerNoteBand`, which embeds this card as its result summary while
   * rendering both coaching slots itself — ungated there, the clue appears twice on one screen.
   */
  showClue?: boolean
  onEdit?: () => void
  onDeleted?: () => void
  onClaimed?: (result: OwnerClaimResponse) => void
  onAppealed?: () => void
}

/** The author's view of the photo check (readers see DealHunt's `photo_label` badge words instead). */
const OWN_PHOTO_LABEL: Record<Exclude<OwnerPhotoStatus, 'none'>, string> = {
  pending: 'Photo under review',
  verified: 'Photo-verified',
  rejected: 'Photo not accepted',
}

/**
 * The author's own note in any status (§6): status word, the judge's `reader_message` on
 * rejection, the claim CTA on a held note signing in would publish (#64/#121) or on a live
 * anonymous note on the product page (keep it yours, 2026-09-14), edit / delete / appeal. Fetched client-side only when the device cookie exists; never server-rendered.
 */
const OwnNoteCard: React.VFC<OwnNoteCardProps> = ({
  note: hostNote,
  claimed,
  trackOwner,
  location,
  questions = OWNER_DEFAULT_QUESTIONS,
  productHref,
  views = null,
  showClue = true,
  onEdit,
  onDeleted,
  onClaimed,
  onAppealed,
}) => {
  const { busy, error, appealed, locked, appeal, remove, undone, undoError, undoProofread } =
    useOwnNoteActions(hostNote.id, {
      trackOwner,
      onAppealed,
      onDeleted,
    })
  // The proofread Undo swaps the text here at once; the host's note wins again once it moves on
  // (`/me` re-read, an edit). Same /me data as every other line — no server-rendered state.
  const note = shownOwnNote(hostNote, undone)
  // A 409 lock (#123) turns `controls.delete` off, which hides an open delete question too.
  const [confirmDelete, setConfirmDelete] = useState(false)

  const heldForHuman = note.status === 'held_review'
  // #121: the button follows DealHunt's reader_message — an appealed or out-of-resubmits note
  // waits for a moderator even after a sign-in, and says so in its own words.
  const signInPublishes = heldClaimOffered(note, claimed)
  // #126/#129: a live note's dropped edit is explained; an accepted anonymous edit gets the same
  // sign-in button (signing in publishes the edit).
  const liveNotice = liveNoteNotice(note, claimed)
  // A live anonymous note: signing in keeps it the reader's (2026-09-14). My gear carries its own
  // CTA above the shelf, so only the product page's own card offers it.
  const keepOffered = location === 'own_note' && keepClaimOffered(note, claimed)
  const showClaim = (signInPublishes || liveNotice?.kind === 'claim' || keepOffered) && onClaimed
  useKeepSurface(Boolean(keepOffered && onClaimed))
  const heldLead =
    liveNotice?.kind === 'claim'
      ? liveNotice.lead
      : note.reader_message ?? OWNER_HELD_REVIEW_MESSAGE
  const claimLead = keepOffered ? keepClaimLead(views) : heldLead
  // Readers see a published note only — every other status is the author's alone.
  const isPublic = note.status === 'published'
  // Only a live note is read; the row needs a DealHunt with #140 (absent = nothing to say).
  const views_ = isPublic ? viewsSummary(views) : null
  const surface = useOwnerSurfaceTrack()
  const viewsShown = views_ ? views_.shown : null
  const viewsTotal = views_?.total ?? null
  useEffect(() => {
    if (viewsShown === null) {
      return
    }
    surface('views_shown', {
      location,
      kind: viewsShown ? 'count' : 'starting',
      view_count: viewsTotal,
      target_product_id: note.node_id,
      ...(claimed ? { auth_state: 'linked' } : {}),
    })
    // Once per note per mount — the count itself changing is not a new render of the row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note.id, viewsShown])
  /**
   * #151 — the author's invitation to add to their note. `recognition` is never repeated on this
   * card at all: the author read it at publish, and the return card owns it afterwards.
   *
   * Suppressed only where the host renders the coaching itself (`showClue={false}` from the
   * band). Everywhere else it shows — a card the author is looking at is where an invitation to
   * edit belongs, and the card carries the Edit button that acts on it.
   *
   * DealHunt recomputes `coaching` on every read and expires the clue 14 days after the note was
   * judged (immediately once the author edits), so it is never cached or re-ranked client-side.
   */
  const clue = showClue ? note.coaching?.clue ?? null : null
  const cluePromptId = clue?.prompt_id ?? null
  useEffect(() => {
    if (!cluePromptId) {
      return
    }
    surface('coaching_shown', {
      location,
      kind: 'clue',
      prompt_id: cluePromptId,
      target_product_id: note.node_id,
      ...(claimed ? { auth_state: 'linked' } : {}),
    })
    // Once per note per mount, like the views row above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [note.id, cluePromptId])
  // #123: no edit / delete on a note a moderator owns (hidden, removed) or after a 409 said so.
  const controls = locked ? { edit: false, delete: false } : ownNoteControls(note.status)
  const canEdit = Boolean(onEdit) && controls.edit
  // Not `canEdit`: the band's result card has no Edit, and Undo needs no form.
  const undoOffered = proofreadUndoOffered(note, controls)
  const canDelete = Boolean(onDeleted) && controls.delete
  const hasActions = Boolean(
    canEdit || productHref || canDelete || appealed || note.status === 'rejected'
  )

  return (
    <article className={clsx(styles.note, styles.ownNote)} aria-label="Your review">
      <header className={styles.noteHead}>
        <OwnerHint hint={OWN_PSEUDONYM_HINT}>
          <span className={styles.handle}>{note.handle ? `@${note.handle}` : 'You'}</span>
        </OwnerHint>
        <OwnerHint hint={statusHint(note.status, claimed, signInPublishes)}>
          <span className={clsx(styles.badge, styles[`status_${note.status}`])}>
            {ownStatusLabel(note.status)}
          </span>
        </OwnerHint>
        {note.photo_status !== 'none' && (
          <OwnerHint hint={OWN_PHOTO_HINT[note.photo_status]}>
            <span
              className={clsx(styles.badge, {
                [styles.badgeVerified]: note.photo_status === 'verified',
              })}
            >
              {OWN_PHOTO_LABEL[note.photo_status]}
            </span>
          </OwnerHint>
        )}
        <OwnerHint
          hint={isPublic ? OWN_CARD_HINT : OWN_PRIVATE_HINT}
          align="end"
          className={styles.noteMetaHint}
        >
          <span className={styles.noteMeta}>
            {isPublic ? 'Readers see only your review' : OWN_PRIVATE_LABEL}
          </span>
        </OwnerHint>
      </header>
      <p className={styles.ownershipLine}>
        {ownershipLine(note.answers, questions, note.published_at)}
      </p>
      <p className={styles.noteText}>{note.note}</p>
      {note.proofread === true && (
        <p className={styles.proofread}>
          {OWN_PROOFREAD_LINE}
          {undoOffered && (
            <>
              {' '}
              <button
                type="button"
                className={styles.proofreadUndo}
                disabled={busy}
                onClick={() => void undoProofread(note)}
              >
                {OWN_PROOFREAD_UNDO}
              </button>
            </>
          )}
        </p>
      )}
      {undoError && <p className={styles.error}>{undoError}</p>}
      <ReviewPhotos note={note} productName={null} />
      <NoteAnswers answers={note.answers} questions={questions} />
      {views_ && <ViewsRow summary={views_} />}
      {liveNotice?.kind === 'checking' && <p className={styles.fine}>{OWN_EDIT_CHECKING_LINE}</p>}
      {liveNotice?.kind === 'message' && <p className={styles.readerMessage}>{liveNotice.text}</p>}
      {note.status === 'rejected' && note.reader_message && (
        <p className={styles.readerMessage}>{note.reader_message}</p>
      )}
      {note.status === 'held_retry' && (
        <p className={styles.fine}>{note.reader_message ?? OWN_HELD_RETRY_LINE}</p>
      )}
      {heldForHuman && !signInPublishes && note.reader_message && (
        <p className={styles.fine}>{note.reader_message}</p>
      )}
      {note.status === 'hidden' && <p className={styles.fine}>{OWN_HIDDEN_LINE}</p>}
      <CoachingLines coaching={clue ? { recognition: null, clue } : null} show="clue" />
      {showClaim && (
        <ClaimCta
          trackOwner={trackOwner}
          location={location}
          lead={claimLead}
          fine={keepOffered ? KEEP_CLAIM_FINE : undefined}
          onClaimed={onClaimed}
          className={styles.ownClaim}
        />
      )}
      {hasActions && (
        <div className={styles.ownActions}>
          {canEdit && (
            <button type="button" className={styles.ownBtn} disabled={busy} onClick={onEdit}>
              {note.status === 'rejected' ? 'Edit and resend' : 'Edit'}
            </button>
          )}
          {productHref && (
            <a
              href={ownersDeepLink(productHref)}
              className={styles.ownBtn}
              target="_blank"
              rel="noopener noreferrer"
            >
              {SEE_ON_PAGE}
            </a>
          )}
          {note.status === 'rejected' && !appealed && (
            <button
              type="button"
              className={styles.ownBtn}
              disabled={busy}
              onClick={() => void appeal()}
            >
              {'Ask for a human look'}
            </button>
          )}
          {appealed && <span className={styles.fine}>{'Sent to a moderator.'}</span>}
          {canDelete &&
            (confirmDelete ? (
              <>
                <span className={styles.fine}>{OWN_DELETE_COPY.question}</span>
                <button
                  type="button"
                  className={clsx(styles.ownBtn, styles.ownBtnDanger)}
                  disabled={busy}
                  onClick={() => void remove()}
                >
                  {OWN_DELETE_COPY.confirm}
                </button>
                <button
                  type="button"
                  className={styles.ownBtn}
                  disabled={busy}
                  onClick={() => setConfirmDelete(false)}
                >
                  {OWN_DELETE_COPY.keep}
                </button>
              </>
            ) : (
              <button
                type="button"
                className={styles.ownBtn}
                disabled={busy}
                onClick={() => setConfirmDelete(true)}
              >
                {OWN_DELETE_COPY.button}
              </button>
            ))}
        </div>
      )}
      {error && <p className={styles.error}>{error}</p>}
    </article>
  )
}

export default OwnNoteCard
